/**
 * THE ERASE: the chart wipes itself right to left before a session begins.
 *
 * <h2>What it is for, and why it is not decoration</h2>
 *
 * A session asks an operator to decide bar by bar without the benefit of hindsight. Snapping
 * straight from a thirty-year chart to a near-empty one states that, but only in the way a page
 * refresh states it — the reader's last impression is still the whole shape of the move they are
 * about to be asked about. Watching it be removed is the difference between being told the
 * history is gone and seeing it go.
 *
 * <pre>
 *   ERASING   the chart as it was, wiping right to left, y axis tracking the shrinking range
 *   EMPTY     NOTHING AT ALL -- no price, no pillars -- held for two seconds
 *   PRIMED    the vertical pillars appear at once, standing across the session's date range
 *   LIVE      the ordinary replay, unveiling left to right from the session's first bar
 * </pre>
 *
 * <h2>Why EMPTY exists, and why the pillars are not drawn before it ends</h2>
 *
 * The pillars used to stand through the whole wipe, because the chart's own option carries them
 * from the moment the session's timeline lands. So the sequence read as "the price is being
 * removed from a chart that already knows the answer", and the structure — the thing the operator
 * is about to trade against — arrived with no moment of its own.
 *
 * <p>Wipe, then WAIT on a canvas holding nothing, then put the structure up in one frame. The two
 * seconds are the whole point of the pause: an empty chart that is instantly repainted is a flicker,
 * and an empty chart held long enough to read is a statement that there is nothing here yet.
 *
 * <h2>IT IS TIME-BOXED, not one bar per frame, and that is forced rather than chosen</h2>
 *
 * "Pop the last bar each frame" is the obvious reading and it cannot work: SPY is 8,466 bars, so
 * at 60fps the wipe would take two minutes and twenty seconds. A visual effect has to be over in
 * about a second, so the loop pops as many bars per frame as the duration demands —
 * {@link erasePlan} is that arithmetic, and on SPY it is about 118 bars a frame.
 *
 * <p>The frames are what the eye sees; the bar count is what the clock demands. Fixing the first
 * and deriving the second is the only arrangement in which the wipe takes the same time on a
 * 250-bar upload and a 33-year series — which it must, because it is a transition and not a
 * progress bar.
 *
 * <h2>The y axis rescales for free, and that is a property of the chart rather than this module</h2>
 *
 * The price axis is declared `{scale: true}` with no `min`/`max`, so ECharts recomputes its extent
 * from whatever data the series currently holds. Each frame hands it a shorter series, so the axis
 * tracks the shrinking range without anything here asking it to. Worth knowing before somebody
 * "fixes" that axis to a fixed range for some other reason.
 */

/** The phases, in the order they happen. */
export const INTRO_PHASE = Object.freeze({
  ERASING: 'ERASING',
  EMPTY: 'EMPTY',
  PRIMED: 'PRIMED',
  LIVE: 'LIVE',
});

/**
 * How long the wipe takes, whatever the series length.
 *
 * <p>1.2 seconds: long enough to read as deliberate, short enough that nobody waits for it. A
 * transition that outstays its welcome is one the operator learns to click through.
 */
export const INTRO_ERASE_MS = 1200;

/**
 * How long the chart is held EMPTY — no price, no structure — before the pillars go up.
 *
 * <p>Two seconds, which is long for a transition and is meant to be: this is the beat that says
 * the history has been removed, and a pause an eye can register is the only thing that
 * distinguishes it from a repaint. The wipe is 1.2s of motion; this is the stillness after it.
 */
export const INTRO_EMPTY_MS = 2000;

/**
 * How long the PRIMED canvas is held before the bars start.
 *
 * <p>Without a hold the primed canvas exists for one frame and the whole point of it — pillars
 * standing in empty space, the structure known before any price — is never seen.
 */
export const INTRO_HOLD_MS = 700;

/**
 * The hold, scaled to the sequence it belongs to.
 *
 * <p>At the default budget this is exactly {@link INTRO_HOLD_MS}. A sequence configured to be
 * fast — a demo set to flick through, or a test that must not be a wait — should not then pause
 * for seven hundred milliseconds in the middle of it: the hold is part of the transition, not a
 * fixed interruption, so it moves with the rest.
 */
export const holdFor = (budgetMs) => {
  const budget = Number.isFinite(budgetMs) && budgetMs > 0 ? budgetMs : INTRO_ERASE_MS;
  return Math.max(1, Math.round(Math.min(INTRO_HOLD_MS,
    INTRO_HOLD_MS * (budget / INTRO_ERASE_MS))));
};

/**
 * The empty wait, scaled to the sequence it belongs to.
 *
 * <p>**At the shipped budget this is exactly two seconds**, which is the duration asked for and
 * the only duration that reaches an operator — nothing overrides `introMs` in the application.
 * It scales for the same reason the hold does: a test that configured a 90ms sequence and then
 * sat still for two whole seconds would be a test nobody runs, and the alternative — a fixed
 * two seconds a test has to mock away — hides the very beat it is meant to be checking.
 */
export const emptyFor = (budgetMs) => {
  const budget = Number.isFinite(budgetMs) && budgetMs > 0 ? budgetMs : INTRO_ERASE_MS;
  return Math.max(1, Math.round(Math.min(INTRO_EMPTY_MS,
    INTRO_EMPTY_MS * (budget / INTRO_ERASE_MS))));
};

/** One animation frame, near enough, for turning a duration into a step size. */
export const FRAME_MS = 16;

/**
 * How many bars to pop per frame, and how many frames that takes.
 *
 * @param totalBars  how many are on screen when the wipe starts
 * @param durationMs the budget
 * @returns `{step, frames}` — `step` is at least 1, so a short series still wipes bar by bar and
 *          simply finishes early rather than dividing into a fractional pop
 */
export function erasePlan(totalBars, durationMs = INTRO_ERASE_MS, frameMs = FRAME_MS) {
  const bars = Number.isFinite(totalBars) && totalBars > 0 ? Math.trunc(totalBars) : 0;
  if (bars === 0) return { step: 1, frames: 0 };

  const budget = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : INTRO_ERASE_MS;
  const span = Number.isFinite(frameMs) && frameMs > 0 ? frameMs : FRAME_MS;
  const frames = Math.max(1, Math.round(budget / span));
  const step = Math.max(1, Math.ceil(bars / frames));
  return { step, frames: Math.ceil(bars / step) };
}

/**
 * The cursor after one frame of wiping.
 *
 * <p>Floors at -1, which is the empty state of an UNFLOORED buffer — the erase runs over the
 * chart as it was, before the session's own floor applies, so it must be able to reach nothing.
 */
export const erasedCursor = (cursor, step) =>
  Math.max(-1, Math.trunc(cursor) - Math.max(1, Math.trunc(step)));

/** Whether the wipe has finished. */
export const isErased = (cursor) => cursor <= -1;

/**
 * Where the chart should look once the canvas is primed.
 *
 * <h2>Why a session does NOT use the ordinary view window</h2>
 *
 * `viewWindowFor` puts the playhead 72% of the way across, so most of the view is the past the
 * engine has seen. That is right for a research replay, where the past is drawn.
 *
 * <p>It is wrong here. A session starts with NO bars behind the playhead — they were just
 * erased — so 72% of the view would be empty canvas on the LEFT, which reads as a chart that has
 * failed to load rather than one waiting to be filled. The session's window is its own date
 * range plus room ahead of it, so the structure the operator is about to trade against is what
 * fills the screen.
 *
 * @param fromIndex   the session's first bar
 * @param toIndex     its last
 * @param axisLength  the FULL axis, padding included
 * @param leadBars    how much empty canvas to show past the session's end
 * @returns `{startValue, endValue}` as bar indices — never percentages, which `EChartComponent`'s
 *          datazoom handler does not record, so the next option rebuild would snap the view back
 */
export function primedViewWindow(fromIndex, toIndex, axisLength, leadBars = 40) {
  const total = Number.isFinite(axisLength) && axisLength > 0 ? Math.trunc(axisLength) : 0;
  if (total === 0) return null;

  const from = Math.max(0, Math.min(Math.trunc(fromIndex) || 0, total - 1));
  const to = Math.max(from, Math.min(Math.trunc(toIndex) || from, total - 1));
  const lead = Math.max(0, Math.trunc(leadBars));

  // A few bars of run-up on the left, so the session's first candle does not appear flush against
  // the axis — a bar on the very edge is hard to read and easy to mistake for a clipped one.
  const start = Math.max(0, from - Math.min(5, from));
  const end = Math.min(total - 1, to + lead);
  return { startValue: start, endValue: end };
}

/** A year of trading days, near enough: the span the forward animation is watched at. */
export const SESSION_VIEW_BARS = 250;

/** How much empty canvas stays ahead of the newest bar. */
export const SESSION_VIEW_LEAD = 20;

/**
 * Where the playhead lands after a pan: a quarter of the way across.
 *
 * <p>So three quarters of the window is the canvas the bars are about to arrive on, with the
 * pillars already standing in it. That ratio is the whole point of panning in jumps rather than
 * continuously — see {@link sessionViewWindow}.
 */
export const PAN_HEAD_FRACTION = 0.25;

/** The run-up on the left, shared with {@link primedViewWindow} so the two agree at bar one. */
const RUN_UP_BARS = 5;

/**
 * THE SLIDING WINDOW: about a year of bars, panning forward as the playhead advances.
 *
 * <h2>Why the primed view cannot also be the animation view</h2>
 *
 * The primed canvas shows the session's WHOLE range, because the thing being shown is the
 * structure standing across it — a reader is being told what the session contains. A two-year
 * session at that scale is 500 candles wide, and a candle appearing in it is a pixel. The
 * animation is the opposite question — what is this bar doing — so it is watched at about a year.
 *
 * <p>The zoom between the two is deliberate and is the handover from "here is the session" to
 * "here is the bar".
 *
 * <h2>It ANCHORS first and slides second</h2>
 *
 * <pre>
 *   bar 1 .. 230     [ session start ............................ ] window fixed, playhead walks
 *   bar 231 onward   [ ........ playhead + lead ] window slides, oldest bars leave on the left
 * </pre>
 *
 * Anchoring first is what stops the session opening on 250 bars of blank canvas to the LEFT of
 * its own first bar: the span is the same either way, so the only question is which end is
 * pinned, and at bar one the answer has to be the start.
 *
 * @param playheadIndex the newest revealed bar, in axis columns
 * @param fromIndex     the session's first bar
 * @param axisLength    the FULL axis, synthetic padding included
 * @returns `{startValue, endValue}` as bar INDICES — percentages are not recorded by
 *          `EChartComponent`'s datazoom handler and are lost on the next option rebuild
 */
export function slidingViewWindow(playheadIndex, fromIndex, axisLength,
  spanBars = SESSION_VIEW_BARS, leadBars = SESSION_VIEW_LEAD) {
  const total = Number.isFinite(axisLength) && axisLength > 0 ? Math.trunc(axisLength) : 0;
  if (total === 0) return null;

  const span = Math.max(1, Math.trunc(spanBars));
  const lead = Math.max(0, Math.trunc(leadBars));
  const from = Math.max(0, Math.min(Math.trunc(fromIndex) || 0, total - 1));
  const head = Math.max(from, Math.min(Math.trunc(playheadIndex) || 0, total - 1));

  const anchor = Math.max(0, from - Math.min(RUN_UP_BARS, from));
  // The right edge is whichever is further on: the anchored window's own end, or the playhead
  // with its lead. Taking the max is the whole of "fixed, then sliding" — no mode, no threshold
  // to cross, and no frame on which the two disagree about where the window is.
  const end = Math.min(total - 1, Math.max(anchor + span, head + lead));
  const start = Math.max(0, end - span);
  return { startValue: start, endValue: end };
}

/** The window a session OPENS the animation on: anchored at its own first bar. */
export function openingViewWindow(fromIndex, axisLength, spanBars = SESSION_VIEW_BARS) {
  const total = Number.isFinite(axisLength) && axisLength > 0 ? Math.trunc(axisLength) : 0;
  if (total === 0) return null;
  const span = Math.max(1, Math.trunc(spanBars));
  const from = Math.max(0, Math.min(Math.trunc(fromIndex) || 0, total - 1));
  const start = Math.max(0, from - Math.min(RUN_UP_BARS, from));
  const end = Math.min(total - 1, start + span);
  return { startValue: Math.max(0, end - span), endValue: end };
}

/**
 * THRESHOLD PANNING: the window holds still, and JUMPS when the playhead runs off its edge.
 *
 * <h2>Why not keep the newest bar pinned to the right</h2>
 *
 * That is what {@link slidingViewWindow} does, and on a continuous run it is the wrong thing
 * twice over. Every bar moves the whole chart one column left, so the operator is reading price
 * action that is sliding under their eyes — and the structure the session exists to trade
 * against is always just off the right edge, entering the view one column at a time. A pillar
 * you can see coming for seventy bars is a pillar you can plan for; a pillar that appears at the
 * edge and is immediately under the playhead is a surprise.
 *
 * <pre>
 *   playhead inside the window        the window does not move at all
 *   playhead reaches the right edge   ONE jump: the playhead lands a quarter across
 *   [ 25% behind | ................ 75% of canvas the bars are about to arrive on ]
 * </pre>
 *
 * <h2>Both directions, because the transport steps backwards</h2>
 *
 * A playhead that walks off the LEFT edge — Arrow Left, or Previous Trade — is just as invisible
 * as one off the right, and re-centring it is the same operation. Without that half, stepping
 * back out of the window leaves the operator looking at bars nobody is pointing at.
 *
 * @param playheadIndex the newest revealed bar, in axis columns
 * @param fromIndex     the session's first bar, for the window it opens on
 * @param current       the window in force, or null to open one
 * @param axisLength    the FULL axis, synthetic padding included
 * @returns `{startValue, endValue}` — the SAME values as `current` when nothing needs to move, so
 *          a caller comparing against what it last dispatched sends nothing
 */
export function sessionViewWindow(playheadIndex, fromIndex, current, axisLength,
  spanBars = SESSION_VIEW_BARS, headFraction = PAN_HEAD_FRACTION,
  marginBars = SESSION_VIEW_LEAD) {
  const total = Number.isFinite(axisLength) && axisLength > 0 ? Math.trunc(axisLength) : 0;
  if (total === 0) return null;

  if (!current || !Number.isFinite(current.startValue) || !Number.isFinite(current.endValue)) {
    return openingViewWindow(fromIndex, total, Math.max(1, Math.trunc(spanBars)));
  }

  /*
   * THE SPAN IS THE CURRENT WINDOW'S, not the constant's.
   *
   * <p>`spanBars` sizes the OPENING window, above. Once there is a window to measure against,
   * the pan keeps whatever width that window has -- which matters because the window may be the
   * OPERATOR's: a reader who zoomed in to sixty bars and then let the playhead run off the edge
   * should get sixty bars back, re-centred, not be yanked out to the 250-bar default.
   *
   * <p>For the animation's own window the two are the same number, so this changes nothing
   * there. It is the adoption of a manual gesture that makes them differ.
   */
  const span = Math.max(1, current.endValue - current.startValue);

  const head = Math.max(0, Math.min(Math.trunc(playheadIndex) || 0, total - 1));
  // The margin is why the newest bar is never flush against the frame: the pan fires while there
  // is still a sliver of canvas ahead of it, so a candle is never drawn half-clipped at the edge.
  //
  // PROPORTIONAL on a narrow window: a flat 20 bars inside a 60-bar view would fire the pan a
  // third of the way across, so a reader who zoomed in would be re-centred almost every bar.
  const margin = Math.max(0, Math.min(
    Math.trunc(marginBars), Math.max(1, Math.round(span * 0.08)), span - 1));
  const inside = head >= current.startValue && head <= current.endValue - margin;
  if (inside) return { startValue: current.startValue, endValue: current.endValue };

  const fraction = Number.isFinite(headFraction)
    ? Math.min(0.9, Math.max(0, headFraction)) : PAN_HEAD_FRACTION;
  let start = Math.max(0, head - Math.round(span * fraction));
  let end = start + span;
  // Clamped at the right-hand end of the axis, where the window can no longer be positioned by
  // the playhead and the playhead travels across it instead. Without this the last bars of a
  // session would scroll off into synthetic columns that do not exist.
  if (end > total - 1) {
    end = total - 1;
    start = Math.max(0, end - span);
  }
  return { startValue: start, endValue: end };
}
